/**
 * Pure encode/parse logic for the Communicator deep-link state — the slice of
 * view state the communicator persists in the URL so a reload (or a shared
 * link) restores the same group, context (student / group channels), tab and
 * conversation preselect.
 *
 * Deliberately decoupled from React/Apollo/`react-router` (same design as
 * `groupDeepLink`) so round-trip and totality can be property-tested in
 * isolation. `useCommunicatorDeepLink` is the thin React wrapper.
 *
 * Conventions:
 *   - `slack` is the default tab and is **omitted** from the URL.
 *   - Every field omits its default/empty value from the encoded params.
 *   - Parsing is **total**: each field falls back to its own safe default on
 *     unrecognized input, so {@link parse} never throws.
 *   - Parsed state is **normalized** (see {@link normalize}): `student` and
 *     `ctx=channels` are mutually exclusive (student wins), context fields are
 *     meaningless without a selected group, and the mail tab does not exist in
 *     the channels context.
 *   - ТЗ 0081: вкладка `sms` бывает только у выбранного СТУДЕНТА — SMS
 *     уходит человеку, у группы и преподавателя её нет.
 */

import { PATHS } from 'router/config';

/**
 * Main-area tab. `mail` is unavailable in the channels context; `sms`
 * (ТЗ 0081) exists only for a selected student.
 */
export type CommunicatorTab = 'slack' | 'mail' | 'tasks' | 'sms';

/** Middle-column context: '' = whole group / none, 'channels' = group channels. */
export type CommunicatorCtx = '' | 'channels';

/** URL-encoded view state that survives a reload. */
export interface ICommunicatorDeepLinkState {
  /** Selected group id. Default `''` (nothing selected). */
  group: string;
  /** Selected student id. Default `''`. Mutually exclusive with `ctx`. */
  student: string;
  /**
   * Selected teacher id (ТЗ 0032). Собеседник в коммуникаторе перестал быть
   * равен студенту: у группы есть ещё и преподаватели. Взаимоисключающий со
   * `student` и `ctx` — открыт ровно один контекст.
   */
  teacher: string;
  /** `'channels'` when the pinned "group channels" item is selected. */
  ctx: CommunicatorCtx;
  /** Active tab. Default `slack`, represented by an absent `tab` param. */
  tab: CommunicatorTab;
  /** Conversation preselect inside the active context. Opaque id. */
  conv: string;
  /** Groups-column search query. Default `''`. */
  query: string;
}

const PARAM = {
  group: 'group',
  student: 'student',
  teacher: 'teacher',
  ctx: 'ctx',
  tab: 'tab',
  conv: 'conv',
  query: 'q',
} as const;

const DEFAULT_TAB: CommunicatorTab = 'slack';
const TAB_IDS: readonly CommunicatorTab[] = ['slack', 'mail', 'tasks', 'sms'];
const CTX_CHANNELS: CommunicatorCtx = 'channels';

const isTab = (value: string | null): value is CommunicatorTab =>
  value !== null && (TAB_IDS as readonly string[]).includes(value);

/**
 * Enforce the state invariants; parse() always returns a normalized state and
 * encode() expects one (a non-normalized state still encodes, but round-trips
 * to its normalized form):
 *   1. Without a group there is no context: student/teacher/ctx/conv reset.
 *   2. `student`, `teacher` and `ctx=channels` are mutually exclusive —
 *      приоритет student → teacher → channels (ТЗ 0032).
 *   3. The channels context has no mail tab — `mail` falls back to `slack`.
 *   4. `sms` needs a selected student — otherwise it falls back to `slack`.
 */
export const normalize = (
  state: ICommunicatorDeepLinkState,
): ICommunicatorDeepLinkState => {
  const { group } = state;
  const student = group ? state.student : '';
  const teacher = group && !student ? state.teacher : '';
  const ctx: CommunicatorCtx =
    group && !student && !teacher && state.ctx === CTX_CHANNELS
      ? CTX_CHANNELS
      : '';
  const conv = group ? state.conv : '';
  const tab: CommunicatorTab =
    (ctx === CTX_CHANNELS && state.tab === 'mail') ||
    (state.tab === 'sms' && !student)
      ? DEFAULT_TAB
      : state.tab;
  return { group, student, teacher, ctx, tab, conv, query: state.query };
};

/**
 * Encode a deep-link state into {@link URLSearchParams}, omitting defaults
 * (including the `slack` tab convention). `parse(encode(state))` restores an
 * equal state for any normalized input.
 */
export const encode = (state: ICommunicatorDeepLinkState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.group) params.set(PARAM.group, state.group);
  if (state.student) params.set(PARAM.student, state.student);
  if (state.teacher) params.set(PARAM.teacher, state.teacher);
  if (state.ctx) params.set(PARAM.ctx, state.ctx);
  if (state.tab !== DEFAULT_TAB) params.set(PARAM.tab, state.tab);
  if (state.conv) params.set(PARAM.conv, state.conv);
  if (state.query) params.set(PARAM.query, state.query);

  return params;
};

/**
 * Parse a query string (or {@link URLSearchParams}) into a normalized
 * deep-link state. Total and defensive: never throws, every field falls back
 * to its default on unrecognized input.
 */
export const parse = (
  search: string | URLSearchParams,
): ICommunicatorDeepLinkState => {
  const params = new URLSearchParams(search);

  const rawTab = params.get(PARAM.tab);
  const rawCtx = params.get(PARAM.ctx);

  return normalize({
    group: params.get(PARAM.group) ?? '',
    student: params.get(PARAM.student) ?? '',
    teacher: params.get(PARAM.teacher) ?? '',
    ctx: rawCtx === CTX_CHANNELS ? CTX_CHANNELS : '',
    tab: isTab(rawTab) ? rawTab : DEFAULT_TAB,
    conv: params.get(PARAM.conv) ?? '',
    query: params.get(PARAM.query) ?? '',
  });
};

/**
 * Ссылка в Коммуникатор с точечным состоянием (ТЗ 0096): Таск-трекер ведёт
 * сюда из строки группы и из старого `/dashboard?view=comms`. Адрес — из
 * `PATHS`, а не литералом: путь в шаблонной строке — 404, невидимый гейтам.
 */
export const communicatorLink = (
  patch: Partial<ICommunicatorDeepLinkState> = {},
): string => {
  const qs = encode(normalize({ ...parse(''), ...patch })).toString();
  return qs ? `${PATHS.communicator}?${qs}` : PATHS.communicator;
};

/**
 * ТЗ 0121 — шаг стековой навигации коммуникатора на телефоне: группы →
 * список чатов → переписка. Живёт в адресе (`?m=`), рядом с выбором, но НЕ
 * в {@link ICommunicatorDeepLinkState}: на компьютере шага нет, а ссылки
 * (`communicatorLink`) остаются прежними. Без `m` шаг выводится из выбора —
 * так ссылка с компьютера на телефоне открывается на нужном экране.
 */
export type CommunicatorMobileStep = 'groups' | 'chats' | 'thread';

export const MOBILE_STEP_PARAM = 'm';

const MOBILE_STEPS: readonly CommunicatorMobileStep[] = [
  'groups',
  'chats',
  'thread',
];

/**
 * Шаг из адреса. Тотальный: неизвестное значение — как отсутствие; шаг
 * глубже, чем позволяет выбор (чаты без группы), — до ближайшего возможного.
 */
export const parseMobileStep = (
  search: string | URLSearchParams,
  state: ICommunicatorDeepLinkState,
): CommunicatorMobileStep => {
  const raw = new URLSearchParams(search).get(MOBILE_STEP_PARAM);
  const explicit = (MOBILE_STEPS as readonly string[]).includes(raw || '')
    ? (raw as CommunicatorMobileStep)
    : null;
  if (!state.group) return 'groups';
  return explicit ?? deriveMobileStep(state);
};

/**
 * Шаг, который следует из одного выбора (без `?m=`): нет группы — группы;
 * открытый собеседник, диалог или «Задачи» — переписка; одна группа — её
 * список чатов.
 */
export const deriveMobileStep = (
  state: ICommunicatorDeepLinkState,
): CommunicatorMobileStep => {
  if (!state.group) return 'groups';
  if (state.student || state.teacher || state.conv || state.tab === 'tasks') {
    return 'thread';
  }
  return 'chats';
};

/** Сменился ли выбор (группа / собеседник / контекст) — тогда шаг из адреса
 *  уже не про него. */
export const isSelectionChange = (
  prev: ICommunicatorDeepLinkState,
  next: ICommunicatorDeepLinkState,
): boolean =>
  prev.group !== next.group ||
  prev.student !== next.student ||
  prev.teacher !== next.teacher ||
  prev.ctx !== next.ctx;

/** Шаг, на который ведёт «Назад». */
export const parentMobileStep = (
  step: CommunicatorMobileStep,
): CommunicatorMobileStep => (step === 'thread' ? 'chats' : 'groups');
